// The shop's own entry. It loads only what the shop needs (no Manager screens), so customers download far less.
import '../polyfills.js';
// Second (right after the polyfill): the artist studio's test copy, when the shop is inside it (src/studio/sandbox.js). Does nothing otherwise.
import '../studio/sandbox.js';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Shop from './Shop.jsx';
import StaffBar, { StaffSwitch } from './StaffBar.jsx';
import { managerAddress, staffApi, startStoreSession, storeMediaPass } from './session.js';
import { ViewerControlsContext, viewerAccess } from '../viewerAccess.js';
import { reloadOnStaleBuild } from '../reloadOnStaleBuild.js';

reloadOnStaleBuild();

const VIEW_KEY = '3dlabs.storeStaffView';
// Loaded only when a signed-in person opens it, so customers never download the editor.
const Editor = lazy(() => import('./edit/Editor.jsx'));

function StoreRoot() {
  const [addresses, setAddresses] = useState({});
  const [session, setSession] = useState(null);
  const [view, setView] = useState(() => { try { return sessionStorage.getItem(VIEW_KEY) === 'public' ? 'public' : 'manager'; } catch { return 'manager'; } });
  const [product, setProduct] = useState(null);
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(false);
  const [visitors, setVisitors] = useState(null);
  // A renewed media pass redraws the editor's private pictures with it (T23).
  const [, setPassVersion] = useState(0);
  useEffect(() => { const bump = () => setPassVersion((v) => v + 1); window.addEventListener('3dlabs-store-media-pass', bump); return () => window.removeEventListener('3dlabs-store-media-pass', bump); }, []);
  useEffect(() => {
    let current = true;
    fetch('/api/setup/status').then((response) => response.json()).catch(() => ({}))
      .then((status) => { if (!current) return null; setAddresses(status); return startStoreSession(status); })
      .then((result) => { if (current && result) setSession(result); });
    return () => { current = false; };
  }, []);
  // Staff who can see Store analytics get today's and the last 30 days' visitor numbers in the staff bar.
  useEffect(() => {
    if (!session?.can?.analytics && !session?.can?.manage) return undefined;
    const load = () => staffApi('/store/analytics/visitors').then((value) => setVisitors(value?.connected && !value.unavailable ? value : null)).catch(() => setVisitors(null));
    load(); const timer = setInterval(load, 300e3);
    return () => clearInterval(timer);
  }, [session]);
  // The switches act on the product that is on screen and take effect straight away.
  const setSale = async (changes) => {
    try {
      const result = await staffApi(`/store/products/${product.id}/sale`, { method: 'POST', body: changes });
      window.dispatchEvent(new CustomEvent('store-product-updated', { detail: result.product }));
      setNotice('');
    } catch (error) { setNotice(error.message); setTimeout(() => setNotice(''), 5000); }
  };
  const chooseView = (next) => { setView(next); try { sessionStorage.setItem(VIEW_KEY, next); } catch { /* private mode */ } };
  // On the public Store address Manager lives elsewhere; in the Windows app, on the host and on the local network it
  // is this same address, so going back stays inside the app instead of opening a browser.
  const managerBase = managerAddress(addresses);
  const manages = session?.can.manage;
  return <ViewerControlsContext.Provider value={viewerAccess(null, false)}>
    <Shop staff={Boolean(session)} drafts={manages && view === 'manager' ? { load: () => staffApi('/store/shop'), token: storeMediaPass() } : null} onProduct={setProduct} />
    {editing && manages && view === 'manager' && <Suspense fallback={null}><Editor owner={Boolean(session.can.owner)} product={product} onClose={() => setEditing(false)} /></Suspense>}
    {session && !editing && <StaffBar session={session} view={view} onView={chooseView} visitors={visitors}
      onEdit={manages ? () => setEditing(true) : undefined}
      onManager={() => location.assign(`${managerBase}/manager`)}>
      {product && manages && <span className="store-staff-product" aria-label={`Switches for ${product.name}`}>
        {product.live === false && <span className="store-staff-draft" title="Customers cannot see this product yet. Publish it from Edit store.">Draft</span>}
        <StaffSwitch label="Can be purchased" checked={product.sale_state === 'open'} onChange={(on) => setSale({ sale_state: on ? 'open' : 'closed' })} title="Off: the product stays visible but cannot be bought" />
        <StaffSwitch label="Beta teaser" checked={product.sale_state === 'beta'} onChange={(on) => setSale({ sale_state: on ? 'beta' : 'open' })} title="On: shown as coming soon, not purchasable yet" />
        <StaffSwitch label="Launch page" checked={Boolean(product.launch_page)} onChange={(on) => setSale({ launch_page: on })} title="On: the product page becomes a scroll story with animations" />
        <StaffSwitch label="STL download" checked={Boolean(product.stl_download_url)} disabled={!product.model_url} onChange={(on) => setSale({ allow_stl_download: on })} title="On: visitors can download the print file from this page" />
        {notice && <span className="store-staff-notice" role="alert">{notice}</span>}
      </span>}
    </StaffBar>}
  </ViewerControlsContext.Provider>;
}

createRoot(document.getElementById('root')).render(<React.StrictMode><StoreRoot /></React.StrictMode>);
